import type {
  ISiteMapArea,
  ISiteMapConfig,
  ISiteMapEquipment,
  ISiteMapData,
  ISiteMapPoint,
  SiteMapAreaColor,
  SiteMapAreaIcon,
  SiteMapEquipmentType,
} from '@interfaces/ISiteMap';
import { authedFetch, UnauthorizedError } from '@apis/auth';
import { sendLogisticsWrite, type ILogisticsRevisionHandle } from '@apis/logisticsRevision';

const base = (siteId: string) => `/api/sites/${encodeURIComponent(siteId)}/site-map`;

export async function fetchSiteMap(siteId: string, signal?: AbortSignal): Promise<ISiteMapData> {
  const res = await authedFetch(base(siteId), { signal });
  if (res.ok) return (await res.json()) as ISiteMapData;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map failed: ${res.status}`);
}

export async function putSiteMapPlan(
  siteId: string,
  floorId: string,
  revision: ILogisticsRevisionHandle | null,
): Promise<ISiteMapConfig> {
  const res = await sendLogisticsWrite(
    `${base(siteId)}/config`,
    {
      method: 'PUT',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify({ floorId }),
    },
    revision,
  );
  if (res.ok) return ((await res.json()) as { config: ISiteMapConfig }).config;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map/config failed: ${res.status}`);
}

export type SiteMapAreaCreateBody = {
  name: string;
  icon: SiteMapAreaIcon;
  color: SiteMapAreaColor;
  points: ISiteMapPoint[];
};

export async function postSiteMapArea(
  siteId: string,
  body: SiteMapAreaCreateBody,
  revision: ILogisticsRevisionHandle | null,
): Promise<ISiteMapArea> {
  const res = await sendLogisticsWrite(
    `${base(siteId)}/areas`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
    revision,
  );
  if (res.ok) return ((await res.json()) as { area: ISiteMapArea }).area;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map/areas failed: ${res.status}`);
}

export type SiteMapAreaPatchBody = Partial<{
  name: string;
  icon: SiteMapAreaIcon;
  color: SiteMapAreaColor;
  materials: string[];
}>;

export async function patchSiteMapArea(
  siteId: string,
  areaId: string,
  patch: SiteMapAreaPatchBody,
  revision: ILogisticsRevisionHandle | null,
): Promise<void> {
  const res = await sendLogisticsWrite(
    `${base(siteId)}/areas/${encodeURIComponent(areaId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    },
    revision,
  );
  if (res.ok) return;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map/areas/:areaId failed: ${res.status}`);
}

export async function deleteSiteMapArea(
  siteId: string,
  areaId: string,
  revision: ILogisticsRevisionHandle | null,
): Promise<void> {
  const res = await sendLogisticsWrite(
    `${base(siteId)}/areas/${encodeURIComponent(areaId)}`,
    { method: 'DELETE' },
    revision,
  );
  if (res.ok) return;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map/areas/:areaId failed: ${res.status}`);
}

export type SiteMapEquipmentCreateBody = {
  type: SiteMapEquipmentType;
  name: string;
  position: ISiteMapPoint;
  radius: number;
};

export async function postSiteMapEquipment(
  siteId: string,
  body: SiteMapEquipmentCreateBody,
  revision: ILogisticsRevisionHandle | null,
): Promise<ISiteMapEquipment> {
  const res = await sendLogisticsWrite(
    `${base(siteId)}/equipment`,
    {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body),
    },
    revision,
  );
  if (res.ok) return ((await res.json()) as { equipment: ISiteMapEquipment }).equipment;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map/equipment failed: ${res.status}`);
}

export type SiteMapEquipmentPatchBody = Partial<{
  name: string;
  position: ISiteMapPoint;
  radius: number;
}>;

export async function patchSiteMapEquipment(
  siteId: string,
  equipmentId: string,
  patch: SiteMapEquipmentPatchBody,
  revision: ILogisticsRevisionHandle | null,
): Promise<void> {
  const res = await sendLogisticsWrite(
    `${base(siteId)}/equipment/${encodeURIComponent(equipmentId)}`,
    {
      method: 'PATCH',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(patch),
    },
    revision,
  );
  if (res.ok) return;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map/equipment/:equipmentId failed: ${res.status}`);
}

export async function deleteSiteMapEquipment(
  siteId: string,
  equipmentId: string,
  revision: ILogisticsRevisionHandle | null,
): Promise<void> {
  const res = await sendLogisticsWrite(
    `${base(siteId)}/equipment/${encodeURIComponent(equipmentId)}`,
    {
      method: 'DELETE',
    },
    revision,
  );
  if (res.ok) return;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map/equipment/:equipmentId failed: ${res.status}`);
}

export async function clearSiteMap(siteId: string, revision: ILogisticsRevisionHandle | null): Promise<void> {
  const res = await sendLogisticsWrite(base(siteId), { method: 'DELETE' }, revision);
  if (res.ok) return;
  if (res.status === 401) throw new UnauthorizedError();
  throw new Error(`fetch /api/sites/:siteId/site-map failed: ${res.status}`);
}
